import React, { useMemo } from 'react'
import { Select, Icon } from '@st-operations/uikit/src/v2/index'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { useModal } from '_hooks'

import Collapsible from '../collapsible'
import { useEventFeedsContext } from '../../context'
import { doesAttributeSupportAggregation } from '../utils'

import AddAttributesModal from './modal-add-attribute'
import ManageComponent, { useIsSnapshot } from '../../manage-component'
import AttributeChip from './attribute-chip'

import './styles.scss'
import { TYPE_PREBUILT_ATTRIBUTES_V2 } from '../../wizard'

const AttributeConfiguration = ({}, ref) => {
  const {
    schema,
    data,
    id,
    querySectionsCollapsed: { attribute: attributeCollapsed },
    dispatch,
    ACTIONS,
  } = useEventFeedsContext()
  const modal = useModal(false)
  const isSnapshot = useIsSnapshot()

  const prebuilt = data.type === TYPE_PREBUILT_ATTRIBUTES_V2

  const attributes = !prebuilt ? schema?.attributes : schema?.prebuilt_attributes_v2

  const normalizedOptions = useMemo(
    () =>
      Object.keys(attributes || {})
        .filter((key) => typeof attributes[key] !== 'string' && !Array.isArray(attributes[key]))

        .map((key) => {
          const val = attributes[key]
          return Object.keys(val).map((valKey, index) => ({
            label: Object.values(val)?.[index]?.label || valKey,
            value: valKey,
          }))
        })
        .flat()
        .filter((val) => !data.attributes?.includes(val.value))
        .filter((val) =>
          data.aggregations?.length > 0 ? doesAttributeSupportAggregation(val.value) : true
        )
        .filter((f) => (data.type === TYPE_PREBUILT_ATTRIBUTES_V2 ? f.value : f)),
    [schema, data]
  )

  const handleSelectChange = (option, name, value) => {
    dispatch({
      type: ACTIONS.ADD_ATTRIBUTE,
      payload: {
        attribute: value,
      },
    })
  }

  const handleRemoveAttribute = (index) => () => {
    dispatch({
      type: ACTIONS.REMOVE_ATTRIBUTE,
      payload: {
        index,
      },
    })
  }

  const handleRemoveAll = () => {
    dispatch({
      type: ACTIONS.UPDATE_AGGREGATIONS,
      payload: {
        attributes: data.attributes.filter((a) => !a.includes(data.aggregations)),
        aggregations: [],
      },
    })
  }

  const setAttributeCollapsed = (value) => {
    dispatch({ type: ACTIONS.UPDATE_QUERY_SECTION_COLLAPSED, payload: { attribute: !value } })
  }

  const changeElementIndex = (array, from, to) => {
    const newArray = [...array]
    const element = newArray[from]
    newArray.splice(from, 1)
    newArray.splice(to, 0, element)
    return newArray
  }

  const moveChip = (dragIndex, hoverIndex) => {
    dispatch({
      type: ACTIONS.UPDATE_FIELD,
      payload: {
        name: 'attributes',
        value: changeElementIndex(data.attributes, dragIndex, hoverIndex),
      },
    })
  }

  return (
    <Collapsible
      ref={ref}
      title="Attributes"
      onOpenClick={modal.handleOpenModal}
      showOpenButton={!isSnapshot}
      onDeleteAllClick={handleRemoveAll}
      showDeleteAllButton={!isSnapshot && data.attributes?.length > 0}
      managedState={[!attributeCollapsed, setAttributeCollapsed]}
    >
      <ManageComponent>
        <AddAttributesModal isOpen={modal.isOpen} onClose={modal.handleCloseModal} id={id} />
        <Select
          placeholder={
            <div className="attribute-configuration__placeholder-search">
              <Icon icon="search" type="outline" color="#3D3D3D" /> Quick search{' '}
            </div>
          }
          options={normalizedOptions}
          isSearchable
          onChange={handleSelectChange}
          useReactSelect
        />
      </ManageComponent>

      <Choose>
        <When condition={data.attributes?.length === 0}>
          <p
            className="attribute-configuration__empty-text"
            style={isSnapshot ? { marginTop: '-16px' } : {}}
          >
            You haven&apos;t added any attributes yet.
          </p>
        </When>
        <Otherwise>
          <section className="attribute-configuration__selections">
            <ManageComponent>
              <div className="attribute-configuration__selections-header">
                <h2 className="attribute-configuration__selections-title">Selected attributes</h2>
              </div>
            </ManageComponent>
            <DndProvider backend={HTML5Backend}>
              <div
                className="attribute-configuration__chips"
                style={isSnapshot ? { marginTop: '-16px' } : {}}
              >
                <For each="attribute" index="index" of={data.attributes || []}>
                  <AttributeChip
                    key={attribute}
                    attribute={
                      Object.values(schema.attributes)?.reduce(
                        (acc, cur) => ({ ...acc, ...cur }),
                        {}
                      )?.[attribute]?.label || attribute
                    }
                    index={index}
                    id={id}
                    isSnapshot={isSnapshot}
                    onRemoveAttribute={handleRemoveAttribute}
                    moveChip={moveChip}
                  />
                </For>
              </div>
            </DndProvider>
          </section>
        </Otherwise>
      </Choose>
    </Collapsible>
  )
}

export default React.forwardRef(AttributeConfiguration)
